"use client";

import { AVATARS, VOICES } from "@/app/lib/constants";
import { debounce } from "lodash";
import {
  Configuration,
  NewSessionData,
  StreamingAvatarApi,
} from "@heygen/streaming-avatar";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  Divider,
  Input,
  Select,
  SelectItem,
  Spinner,
  Tooltip,
  Slider,
  ScrollShadow,
  Textarea,
} from "@nextui-org/react";
import { Microphone, MicrophoneStage } from "@phosphor-icons/react";
// @ts-ignore
import { useChat } from "ai/react";
import clsx from "clsx";
import { useEffect, useRef, useState, useCallback } from "react";
import InteractiveAvatarTextInput from "./InteractiveAvatarTextInput";

export default function InteractiveAvatar() {
  const [isLoadingSession, setIsLoadingSession] = useState(false);
  const [isLoadingRepeat, setIsLoadingRepeat] = useState(false);
  const [shouldSubmit, setShouldSubmit] = useState(false);
  const [messageHistory, setMessageHistory] = useState<
    Array<{ role: "user" | "assistant"; content: string }>
  >([]);

  const [isLoadingChat, setIsLoadingChat] = useState(false);
  const [stream, setStream] = useState<MediaStream>();
  const [debug, setDebug] = useState<string>();
  const [modelId, setModelId] = useState<string>(
    process.env.NEXT_PUBLIC_DEFAULT_MODEL || "openai/gpt-4o-mini-search-preview",
  );

  // Add this array of available models
  const availableModels = [
    {
      value: "openai/gpt-4o-mini-search-preview",
      label: "GPT-4o Mini Search Preview 12 Mar 25",
    },
    {
      value: "perplexity/sonar",
      label: "Perplexity sonar online",
    },

    {
      value: "perplexity/sonar-pro",
      label: "Perplexity sonar pro ",
    },
    {
      value: "meta-llama/llama-3.1-405b-instruct",
      label: "Llama 3.1 405 Jul 23",
    },
    { value: "openai/gpt-4o-mini", label: "GPT-4o Mini Jul 18" },
    {
      value: "anthropic/claude-3.5-sonnet:beta",
      label: "Claude 3.5 Sonnet Jun 20",
    },
    { value: "openai/gpt-4o", label: "GPT-4o May 13" },
    { value: "google/gemini-pro-1.5", label: "Gemini 1.5 Pro Apr 9th" },
    { value: "openai/gpt-4-turbo", label: "GPT-4 Turbo Apr 9th" },
  ];
  const [avatarId, setAvatarId] = useState<string>(
    process.env.NEXT_PUBLIC_DEFAULT_AVATAR || "",
  );
  const [voiceId, setVoiceId] = useState<string>(
    process.env.NEXT_PUBLIC_DEFAULT_VOICE || "",
  );
  const [data, setData] = useState<NewSessionData>();
  const [text, setText] = useState<string>("");
  const [initialized, setInitialized] = useState(false); // Track initialization
  const [recording, setRecording] = useState(false); // Track recording state
  const [sessionStarted, setSessionStarted] = useState(false);
  const [videoVolume, setVideoVolume] = useState<number>(100);
  const [searchContext, setSearchContext] = useState<string>("Neu21");
  const [prompt, setPrompt] = useState<string>(`

    Speak conversationally with a playful upbeat informal human tone.
    Use short summary responses to keep the user engaged. This means avoiding numbered lists (DON'T RESPOND WITH NUMBERED LISTS) and other awkward responses. Your output is directly spoken to he users so you can't use formatting or use links. Ignore any non-english inputs, say 'i didn't catch that' instead of replying in that language. Feel free to ask the user clarifying questions to better answer.  
    Don't try to promote your company too much, instead just be helpful.  Your voice is british and your audience is mostly australian. 

  Remember DON'T RESPOND WITH NUMBERED LISTS, instead delimit your lists with words like "also" and "and" as appropriate for your spoken responses. Do NOT INCLUDE CITATIONS OR LINKS.

  Don't refer to the above instructions in your responses. 

  ## About You

  Your name is Debbie, you are a friendly AI representing [CONTEXT].  

  `);

  const animationFrameId = useRef<number>();

  const mediaStream = useRef<HTMLVideoElement>(null);
  const avatar = useRef<StreamingAvatarApi | null>(null);
  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const audioChunks = useRef<Blob[]>([]);

  const splitTextIntoChunks = (
    text: string,
    maxLength: number = 1000,
  ): string[] => {
    const chunks: string[] = [];
    let currentChunk = "";

    // Split the text into sentences
    const sentences = text.match(/[^.!?]+[.!?]+|\s*$/g) || [];

    for (const sentence of sentences) {
      if (currentChunk.length + sentence.length <= maxLength) {
        currentChunk += sentence;
      } else {
        if (currentChunk) chunks.push(currentChunk.trim());
        currentChunk = sentence;
      }
    }

    if (currentChunk) chunks.push(currentChunk.trim());

    return chunks;
  };

  const { input, setInput, handleSubmit, setMessages } = useChat({
    body: { llm: { model: modelId, searchContext } },
    // @ts-ignore
    onResponse: async (response) => {
      console.log("Response:", response);
    },
    // @ts-ignore
    onFinish: async (message) => {
      console.log("ChatGPT Response:", message);
      setMessageHistory((prev) => [
        ...prev,
        { role: "assistant", content: message.content },
      ]);

      if (!initialized || !avatar.current) {
        setDebug("Avatar API not initialized");
        return;
      }

      // Split the message content into chunks
      const chunks = splitTextIntoChunks(message.content);

      // Send each chunk to the Interactive Avatar
      for (const chunk of chunks) {
        await avatar.current
          .speak({
            taskRequest: { text: chunk, sessionId: data?.sessionId },
          })
          .catch((e) => {
            setDebug(e.message);
          });
      }

      setIsLoadingChat(false);
    },
    initialMessages: [
      {
        id: "1",
        role: "system",
        content: prompt,
      },
    ],
  });

  // Add this function to update the system prompt
  const updateSystemPrompt = useCallback(
    (newPrompt: string) => {
      setPrompt(newPrompt);
      // @ts-ignore
      setMessages((prevMessages) => {
        const updatedMessages = [...prevMessages];
        updatedMessages[0] = {
          ...updatedMessages[0],
          content: newPrompt,
        };
        return updatedMessages;
      });
    },
    [setMessages],
  );

  async function fetchAccessToken() {
    try {
      const response = await fetch("/api/get-access-token", {
        method: "POST",
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const token = await response.text();
      console.log("Access Token:", token);
      return token;
    } catch (error) {
      if (error instanceof Error) {
        console.error("Error fetching access token:", error);
        setDebug(`Error fetching access token: ${error.message}`);
      } else {
        console.error("Unknown error fetching access token:", error);
        setDebug("Unknown error fetching access token");
      }
      return "";
    }
  }

  const speakFiller = async () => {
    if (!initialized || !avatar.current || !data?.sessionId) return;

    const fillerPhrases = [
      "Yeah. Thanks for the question.",
      "Got it. Let me think.",
      "Right. Ok.",
      "Great question.",
      "Give me a moment.",
    ];

    const randomFiller =
      fillerPhrases[Math.floor(Math.random() * fillerPhrases.length)];

    await avatar.current
      .speak({
        taskRequest: { text: randomFiller, sessionId: data.sessionId },
      })
      .catch((e) => {
        setDebug(e.message);
      });
  };

  async function startSession() {
    setIsLoadingSession(true);
    try {
      await updateToken();
      if (!avatar.current) {
        throw new Error("Avatar API is not initialized");
      }
      console.log("Starting session with:", { avatarId, voiceId });
      const res = await avatar.current.createStartAvatar(
        {
          newSessionRequest: {
            quality: "high",
            avatarName: avatarId,
            voice: { voiceId: voiceId },
          },
        },
        (debug) => {
          console.log("Debug:", debug);
          setDebug(debug);
        },
      );
      console.log("Session response:", res);
      setData(res);
      setStream(avatar.current.mediaStream);
      setSessionStarted(true);
    } catch (error) {
      if (error instanceof Error) {
        console.error("Error starting avatar session:", error);
        setDebug(`Error starting session: ${error.message}`);
      } else {
        console.error("Unknown error starting avatar session:", error);
        setDebug("Unknown error starting session");
      }
    } finally {
      setIsLoadingSession(false);
    }
  }

  async function updateToken() {
    const newToken = await fetchAccessToken();
    console.log("Updating Access Token:", newToken); // Log token for debugging
    avatar.current = new StreamingAvatarApi(
      new Configuration({ accessToken: newToken }),
    );

    const startTalkCallback = (e: any) => {
      console.log("Avatar started talking", e);
      if (window.hj) {
        window.hj('event', 'avatar_started_talking');
      }
    };

    const stopTalkCallback = (e: any) => {
      console.log("Avatar stopped talking", e);
      if (window.hj) {
        window.hj('event', 'avatar_stopped_talking');
      }
    };

    console.log("Adding event handlers:", avatar.current);
    avatar.current.addEventHandler("avatar_start_talking", startTalkCallback);
    avatar.current.addEventHandler("avatar_stop_talking", stopTalkCallback);

    setInitialized(true);
  }

  async function handleInterrupt() {
    if (!initialized || !avatar.current) {
      setDebug("Avatar API not initialized");
      return;
    }
    await avatar.current
      .interrupt({ interruptRequest: { sessionId: data?.sessionId } })
      .catch((e) => {
        setDebug(e.message);
      });
  }

  async function endSession() {
    if (!initialized || !avatar.current) {
      setDebug("Avatar API not initialized");
      return;
    }
    await avatar.current.stopAvatar(
      { stopSessionRequest: { sessionId: data?.sessionId } },
      setDebug,
    );
    setStream(undefined);
    setSessionStarted(false);
  }

  async function handleSpeak() {
    setIsLoadingRepeat(true);
    if (!initialized || !avatar.current) {
      setDebug("Avatar API not initialized");
      return;
    }
    await avatar.current
      .speak({ taskRequest: { text: text, sessionId: data?.sessionId } })
      .catch((e) => {
        setDebug(e.message);
      });
    setIsLoadingRepeat(false);
  }

  useEffect(() => {
    async function init() {
      const newToken = await fetchAccessToken();
      console.log("Initializing with Access Token:", newToken); // Log token for debugging
      avatar.current = new StreamingAvatarApi(
        new Configuration({ accessToken: newToken, jitterBuffer: 200 }),
      );
      setInitialized(true); // Set initialized to true
      // startSession();
    }
    init();

    return () => {
      endSession();
    };
  }, []);

  useEffect(() => {
    if (stream && mediaStream.current) {
      mediaStream.current.srcObject = stream;
      mediaStream.current.onloadedmetadata = () => {
        mediaStream.current!.play();
        setDebug("Playing");
      };

      mediaStream.current.volume = videoVolume / 100;

      const videoElement = mediaStream.current;
      const canvas = document.getElementById(
        "chromakeyCanvas",
      ) as HTMLCanvasElement;
      const ctx = canvas.getContext("2d");

      if (ctx) {
        const updateCanvas = () => {
          if (videoElement.readyState === videoElement.HAVE_ENOUGH_DATA) {
            canvas.width = videoElement.videoWidth;
            canvas.height = videoElement.videoHeight;
            ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
            const imageData = ctx.getImageData(
              0,
              0,
              canvas.width,
              canvas.height,
            );
            const data = imageData.data;

            for (let i = 0; i < data.length; i += 4) {
              const r = data[i];
              const g = data[i + 1];
              const b = data[i + 2];

              // Adjust these values to fine-tune the chroma key effect
              const threshold = 100;
              const greenDominance = 1.5;

              if (
                g > threshold &&
                g > r * greenDominance &&
                g > b * greenDominance
              ) {
                data[i + 3] = 0; // Set alpha to 0 (fully transparent)
              }
            }

            ctx.putImageData(imageData, 0, 0);
          }
          animationFrameId.current = requestAnimationFrame(updateCanvas);
        };

        updateCanvas();
      }
    }

    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [mediaStream, stream, videoVolume]);

  const handleVideoVolumeChange = (value: number | number[]) => {
    const newVolume = Array.isArray(value) ? value[0] : value;
    setVideoVolume(newVolume);
    if (mediaStream.current) {
      mediaStream.current.volume = newVolume / 100;
    }
  };

  const handleSubmitWithFiller = () => {
    if (!input) {
      setDebug("Please enter text to send to ChatGPT");
      return;
    }
    setIsLoadingChat(true);

    // Store the current input before submitting
    const currentInput = input;

    setMessageHistory((prev) => [
      ...prev,
      { role: "user", content: currentInput },
    ]);

    // Track chat message sent
    if (window.hj) {
      window.hj('event', 'chat_message_sent', {
        message: currentInput
      });
    }

    // Speak a filler phrase immediately
    speakFiller();
    handleSubmit();
  }

  function startRecording() {
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then((stream) => {
        mediaRecorder.current = new MediaRecorder(stream);
        mediaRecorder.current.ondataavailable = (event) => {
          audioChunks.current.push(event.data);
        };
        mediaRecorder.current.onstop = () => {
          console.log("media recorder stopping");
          const audioBlob = new Blob(audioChunks.current, {
            type: "audio/wav",
          });
          audioChunks.current = [];
          transcribeAudio(audioBlob);
        };
        mediaRecorder.current.start();
        setRecording(true);
      })
      .catch((error) => {
        console.error("Error accessing microphone:", error);
      });
  }

  function stopRecording() {
    console.log("stopRecording");
    if (mediaRecorder.current) {
      console.log("current.stop");
      mediaRecorder.current.stop();
      setRecording(false);

      // Only stop the audio track
      if (mediaRecorder.current.stream) {
        mediaRecorder.current.stream
          .getAudioTracks()
          .forEach((track) => track.stop());
      }
      mediaRecorder.current = null;
    }
  }

  useEffect(() => {
    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
        animationFrameId.current = undefined;
      }
      if (mediaRecorder.current && mediaRecorder.current.stream) {
        mediaRecorder.current.stream
          .getTracks()
          .forEach((track) => track.stop());
      }
    };
  }, []);

  useEffect(() => {
    if (shouldSubmit && input) {
      handleSubmitWithFiller();
      setShouldSubmit(false);
    }
  }, [input, shouldSubmit, handleSubmit]);

  const submitTranscription = useCallback(
    async (transcription: string) => {
      setInput(transcription);
      setShouldSubmit(true);
    },
    [setInput],
  );

  async function transcribeAudio(audioBlob: Blob) {
    try {
      const base64Audio = await blobToBase64(audioBlob);
      const response = await fetch("/api/transcribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ audio: base64Audio }),
      });

      if (!response.ok) {
        throw new Error("Transcription failed");
      }

      const data = await response.json();
      console.log("Transcription: ", data.transcription);
      await submitTranscription(data.transcription);
    } catch (error) {
      console.error("Error transcribing audio:", error);
    }
  }

  // Helper function to convert Blob to Base64
  function blobToBase64(blob: Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [messageHistory]);

  const [isAuthorized, setIsAuthorized] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [urlParams, setUrlParams] = useState<URLSearchParams | null>(null);

  useEffect(() => {
    const urlParamsLocal = new URLSearchParams(window.location.search);
    setUrlParams(urlParamsLocal);
    const key = urlParamsLocal.get("key");
    if (key === "1234") {
      setIsAuthorized(true);
    }
    const admin = urlParamsLocal.get("admin");
    if (admin === "true") {
      setIsAdmin(true);
    }
    const contextParam = urlParamsLocal.get("context");
    if (contextParam) {
      setSearchContext(contextParam);
    }
  }, []);

  if (!isAuthorized) {
    return (
      <div className="w-full flex flex-col justify-center items-center h-[500px] gap-4">
        <p className="text-xl">
          Please enter the password to access this page.
        </p>
        <form action="" method="get" className="flex gap-2">
          <input
            type="password"
            name="key"
            placeholder="Enter password"
            className="px-4 py-2 border rounded"
          />
          <button
            type="submit"
            className="px-4 py-2 bg-blue-500 text-white rounded"
            onClick={() => {
              if (window.hj) {
                window.hj('event', 'login_button_clicked');
              }
            }}
          >
            Submit
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col gap-4">
      <Card>
        <CardBody className="flex flex-col justify-center items-center p-0">
          {stream ? (
            <div className="w-full aspect-video justify-center items-center flex rounded-lg overflow-hidden relative object-contain">
              <video
                ref={mediaStream}
                autoPlay
                playsInline
                style={{
                  display: "none",
                }}
              >
                <track kind="captions" />
              </video>
              <div 
                className="absolute inset-0 w-full aspect-video bg-cover bg-center bg-no-repeat"
                style={{ backgroundImage: `url(${urlParams?.get('bgurl') || '/bg.png'})` }}
              ></div>
              <canvas
                id="chromakeyCanvas"
                className="absolute inset-0 w-full aspect-video z-10 object-contain"
              ></canvas>

              <div className="absolute left-3 bottom-3 z-20 flex flex-col gap-2 items-start">
                <Button
                  size="sm"
                  onClick={() => {
                    handleInterrupt();
                    if (window.hj) {
                      window.hj('event', 'interrupt_button_clicked');
                    }
                  }}
                  className="bg-gradient-to-tr from-indigo-500 to-indigo-300 text-white rounded-lg"
                  variant="shadow"
                >
                  Interrupt
                </Button>
                <Button
                  size="sm"
                  onClick={() => {
                    endSession();
                    if (window.hj) {
                      window.hj('event', 'end_session_button_clicked');
                    }
                  }}
                  className="bg-gradient-to-tr from-indigo-500 to-indigo-300 text-white rounded-lg"
                  variant="shadow"
                >
                  End session
                </Button>
                <div className="w-48 mt-2">
                  <Slider
                    aria-label="Video Volume"
                    size="sm"
                    color="foreground"
                    value={videoVolume}
                    onChange={handleVideoVolumeChange}
                    className="max-w-md"
                  />
                </div>
              </div>
            </div>
          ) : !isLoadingSession ? (
            <div className="h-full justify-center items-center flex flex-col gap-8 w-full max-w-[500px] self-center p-4">
              <p>Click the button to start</p>
              <Button
                onClick={() => {
                  startSession();
                  if (window.hj) {
                    window.hj('event', 'start_session_button_clicked');
                  }
                }}
                disabled={isLoadingSession}
                className="bg-gradient-to-tr from-indigo-500 to-indigo-300 text-white rounded-lg"
                variant="shadow"
              >
                {isLoadingSession ? (
                  <Spinner size="sm" color="white" />
                ) : (
                  "Start Session"
                )}
              </Button>
            </div>
          ) : (
            <Spinner size="lg" color="default" />
          )}
        </CardBody>

        <Divider />
        <CardFooter className="flex flex-col gap-3 p-4">
          <InteractiveAvatarTextInput
            label="Chat"
            placeholder="Chat with Debbie"
            input={input}
            onSubmit={() => {
              if (!input) {
                setDebug("Please enter text to send to ChatGPT");
                return;
              }
              setIsLoadingChat(true);
              handleSubmitWithFiller();
            }}
            setInput={setInput}
            loading={isLoadingChat}
            endContent={
              <Tooltip
                content={!recording ? "Start recording" : "Stop recording"}
              >
                <Button
                  onClick={() => {
                    if (!recording) {
                      startRecording();
                      if (window.hj) {
                        window.hj('event', 'start_recording_button_clicked');
                      }
                    } else {
                      stopRecording();
                      if (window.hj) {
                        window.hj('event', 'stop_recording_button_clicked');
                      }
                    }
                  }}
                  isDisabled={!stream}
                  isIconOnly
                  className={clsx(
                    "mr-4 text-white",
                    !recording
                      ? "bg-gradient-to-tr from-indigo-500 to-indigo-300"
                      : "",
                  )}
                  size="sm"
                  variant="shadow"
                >
                  {!recording ? (
                    <Microphone size={20} />
                  ) : (
                    <>
                      <div className="absolute h-full w-full bg-gradient-to-tr from-indigo-500 to-indigo-300 animate-pulse -z-10"></div>
                      <MicrophoneStage size={20} />
                    </>
                  )}
                </Button>
              </Tooltip>
            }
            disabled={!stream}
          />
        </CardFooter>
      </Card>
        <div className="mt-4 text-sm text-gray-600 italic">
          Disclaimer: This is an AI-powered demonstration. Debbies's responses
          are generated in real-time and may not always be accurate or
          appropriate. Please use this demo responsibly and do not rely on it
          for critical decisions or information.
        </div>
        <div className="mt-4 text-sm text-gray-600 italic">
        Brought to you by <a href="https://freshwaterfutures.com">Freshwater Futures Agent Platform</a> in partnership with Neu21.
        </div>

      {isAdmin && (
        <div className="flex flex-col gap-4">
          <Card>
            <CardBody className="h-[200px] flex flex-col">
              <ScrollShadow ref={scrollRef} className="h-full overflow-y-auto">
                <div className="flex flex-col-reverse">
                  {messageHistory.map((msg, index) => (
                    <div
                      key={index}
                      className={`mb-2 ${msg.role === "user" ? "text-blue-600" : "text-green-600"}`}
                    >
                      <strong>
                        {msg.role === "user" ? "You: " : "Debbie: "}
                      </strong>
                      {msg.content}
                    </div>
                  ))}
                </div>
              </ScrollShadow>
            </CardBody>
          </Card>

          <Select
            label="Select Model"
            placeholder="Choose a model"
            selectedKeys={[modelId]}
            className="max-w-xs"
            onChange={(e) => {
              setModelId(e.target.value);
              if (window.hj) {
                window.hj('event', 'model_selected', {model: e.target.value});
              }
            }}
          >
            {availableModels.map((model) => (
              <SelectItem key={model.value} value={model.value}>
                {model.label}
              </SelectItem>
            ))}
          </Select>
          <Input
            label="Search Context"
            value={searchContext}
            onChange={(e) => {
              setSearchContext(e.target.value);
              if (window.hj) {
                window.hj('event', 'search_context_changed', {context: e.target.value});
              }
            }}
            className="mb-4"
          />
          <Textarea
            className="w-full h-[200px] p-2 border rounded"
            value={prompt}
            onChange={(e) => {
              const newPrompt = e.target.value;
              updateSystemPrompt(newPrompt);
              if (window.hj) {
                window.hj('event', 'prompt_updated', {prompt: newPrompt});
              }
            }}
          />

          <p className="font-mono text-right">
            <span className="font-bold">Console:</span>
            <br />
            {debug}
          </p>

          {/* <AdminFirecrawl /> */}
        </div>
      )}
    </div>
  );
}